import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationStart } from '@angular/router';
import { tap, catchError, filter, take } from 'rxjs/operators';
import { throwError, of } from 'rxjs';
import { SubscriptionService } from './subscription.service';
import { TokenService } from './token.service';
import { ConfigService } from './config.service';
import { environment } from '../../environments/environment';

export interface RegisterRequest {
  email: string;
  password: string;
  country: string;
  isBusinessUse?: boolean;
}

export interface ConfirmRegistrationRequest {
  email: string;
  code: string;
  password: string;
}

export interface ResendCodeRequest {
  email: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  token?: string;
  helpSidebarVisible?: boolean;
  user?: {
    email: string;
    isEmailVerified: boolean;
    isTestUser: boolean;
  };
}

export interface User {
  email: string;
  isEmailVerified: boolean;
  isTestUser?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly tokenService = inject(TokenService);
  private readonly apiUrl = `${environment.apiUrl}/auth`;

  // Signals für State Management
  isAuthenticated = signal(false);
  user = signal<User | null>(null);
  errorMessage = signal<string | null>(null);
  errorCode = signal<string | null>(null);
  userMenuOpen = signal(false);
  helpSidebarVisible = signal(true);

  // Token$ delegieren zu TokenService
  get token$() {
    return this.tokenService.token$;
  }

  // Timer für automatischen Token-Ablauf-Check
  private tokenExpiryCheckInterval: any = null;

  constructor() {
    // Token beim Service-Konstruktion laden
    const storedToken = this.tokenService.getToken();
    if (storedToken) {
      this.isAuthenticated.set(true);
      
      // Lade Benutzerinformationen vom /me Endpoint
      this.http.get<User>(`${this.apiUrl}/me`).subscribe({
        next: (userInfo) => {
          this.user.set(userInfo);
        },
        error: (error) => {
          console.error('Fehler beim Laden der Benutzerinformationen:', error);
          // Fallback: Dekodiere Email aus Token
          const email = this.getEmailFromToken();
          if (email) {
            this.user.set({ email, isEmailVerified: true });
          }
        }
      });
      
      this.startTokenExpiryCheck();
      this.setupActivityListeners();
    }

    // Schließe das Menü bei Navigation
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        this.userMenuOpen.set(false);
      });
  }

  /**
   * Registrierung mit Email und Passwort
   */
  register(email: string, password: string, country: string, isBusinessUse: boolean = false) {
    const request: RegisterRequest = { email, password, country, isBusinessUse };
    
    // In Entwicklungsumgebung: Code in Console ausgeben statt Email zu senden
    if (this.isDevelopment()) {
      const verificationCode = this.generateVerificationCode();
      console.log('===========================================');
      console.log('📧 DEVELOPMENT MODE - Verifizierungscode:');
      console.log(`Email: ${email}`);
      console.log(`Country: ${country}`);
      console.log(`Business Use: ${isBusinessUse}`);
      console.log(`Code: ${verificationCode}`);
      console.log('===========================================');
      
      // Speichern für spätere Verifikation - localStorage für Persistenz
      localStorage.setItem(`verificationCode_${email}`, verificationCode);
      localStorage.setItem(`registeredUser_${email}`, JSON.stringify({ email, password, country, isBusinessUse }));
      localStorage.setItem('registrationPassword', password);
      
      return of({
        success: true,
        message: 'Registrierung erfolgreich. Code wurde in der Console ausgegeben.'
      }).pipe(
        tap((response) => {
          this.errorMessage.set(null);
        })
      );
    }
    
    // Produktion: Backend-API verwenden
    return this.http.post<AuthResponse>(`${this.apiUrl}/register`, request).pipe(
      tap((response) => {
        this.errorMessage.set(null);
        if (response.success) {
          // Passwort temporär speichern für Email-Verifikation
          sessionStorage.setItem('registrationPassword', password);
          console.log('Registrierung erfolgreich. Bitte überprüfen Sie Ihre E-Mail.');
        }
      }),
      catchError((error) => {
        console.log('Register Error:', error);
        console.log('Error Details:', error.error);
        const message = error.error?.message || 'Registrierung fehlgeschlagen';
        this.errorMessage.set(message);
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Email-Verifikation mit Code
   */
  confirmRegistration(email: string, code: string, password: string) {
    // In Entwicklungsumgebung: Code lokal überprüfen
    if (this.isDevelopment()) {
      const storedCode = localStorage.getItem(`verificationCode_${email}`);
      if (storedCode === code) {
        // Erfolgreiche Verifikation
        const mockResponse: AuthResponse = {
          success: true,
          message: 'Email verifiziert',
          token: 'mock-jwt-token-' + Date.now()
        };
        
        localStorage.removeItem(`verificationCode_${email}`);
        localStorage.removeItem('registrationPassword');
        
        return of(mockResponse).pipe(
          tap((response) => {
            this.errorMessage.set(null);
            if (response.success && response.token) {
              this.setToken(response.token);
              this.isAuthenticated.set(true);
              this.user.set({ email, isEmailVerified: true });
              this.startTokenExpiryCheck();
              this.setupActivityListeners();
            }
          })
        );
      } else {
        // Falscher Code
        const errorMessage = 'Ungültiger Verifizierungscode';
        this.errorMessage.set(errorMessage);
        return throwError(() => new Error(errorMessage));
      }
    }
    
    // Produktion: Backend-API verwenden
    const request: ConfirmRegistrationRequest = { email, code, password };
    return this.http
      .post<AuthResponse>(`${this.apiUrl}/register/confirm`, request)
      .pipe(
        tap((response) => {
          this.errorMessage.set(null);
          if (response.success && response.token) {
            this.setToken(response.token);
            this.isAuthenticated.set(true);
            this.user.set({ email, isEmailVerified: true });
            this.startTokenExpiryCheck();
            this.setupActivityListeners();
          }
        }),
        catchError((error) => {
          const message = error.error?.message || 'Email-Verifikation fehlgeschlagen';
          this.errorMessage.set(message);
          return throwError(() => new Error(message));
        })
      );
  }

  /**
   * Verifikationscode erneut senden
   */
  resendVerificationCode(email: string) {
    // In Entwicklungsumgebung: Code erneut generieren und in Console ausgeben
    if (this.isDevelopment()) {
      const verificationCode = this.generateVerificationCode();
      console.log('===========================================');
      console.log('📧 DEVELOPMENT MODE - Neuer Verifizierungscode:');
      console.log(`Email: ${email}`);
      console.log(`Code: ${verificationCode}`);
      console.log('===========================================');
      
      // Speichern für spätere Verifikation
      localStorage.setItem(`verificationCode_${email}`, verificationCode);
      
      return of({
        success: true,
        message: 'Verifizierungscode erneut gesendet. Code wurde in der Console ausgegeben.'
      }).pipe(
        tap((response) => {
          this.errorMessage.set(null);
        })
      );
    }

    // Produktion: Backend-API verwenden
    const request: ResendCodeRequest = { email };
    return this.http.post<AuthResponse>(`${this.apiUrl}/register/resend-code`, request).pipe(
      tap((response) => {
        this.errorMessage.set(null);
      }),
      catchError((error) => {
        const message = error.error?.message || 'Code erneut senden fehlgeschlagen';
        this.errorMessage.set(message);
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Login mit Email und Passwort
   */
  login(email: string, password: string) {
    // Immer Backend-API verwenden (auch in Development)
    const request: LoginRequest = { email, password };
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, request).pipe(
      tap((response) => {
        this.errorMessage.set(null);
        this.errorCode.set(null);
        if (response.success && response.token) {
          this.setToken(response.token);
          this.isAuthenticated.set(true);
          
          // ✅ Speichere isTestUser für Maintenance-Check
          this.user.set({
            email,
            isEmailVerified: response.user?.isEmailVerified || true,
            isTestUser: response.user?.isTestUser || false
          });
          
          // Set help sidebar preference from server response
          if (response.helpSidebarVisible !== undefined) {
            this.helpSidebarVisible.set(response.helpSidebarVisible);
          }
          
          this.startTokenExpiryCheck();
          this.setupActivityListeners();
          
          // Lade User Features nach erfolgreichem Login
          this.subscriptionService.getUserFeatures().subscribe();
        }
      }),
      catchError((error) => {
        const message = error.error?.message || 'Login fehlgeschlagen';
        const code = error.error?.error === 'maintenance_active' ? 'MAINTENANCE_ACTIVE' : error.error?.code;
        
        if (!environment.production && code === 'MAINTENANCE_ACTIVE') {
          console.log('[LOGIN] ⚠ Backend: Maintenance aktiv, kein Non-TestUser Login');
        }
        
        this.errorMessage.set(message);
        this.errorCode.set(code);
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Logout - Token löschen
   */
  logout() {
    this.stopTokenExpiryCheck();
    this.removeActivityListeners();
    this.tokenService.clearToken();
    this.isAuthenticated.set(false);
    this.user.set(null);
    this.errorMessage.set(null);
    this.errorCode.set(null);
  }

  /**
   * Token speichern (delegiert zu TokenService)
   */
  setToken(token: string): void {
    this.tokenService.setToken(token);
  }

  /**
   * Token abrufen (delegiert zu TokenService)
   */
  getToken(): string | null {
    return this.tokenService.getToken();
  }

  /**
   * Token-Ablaufdatum verlängern (delegiert zu TokenService)
   */
  refreshTokenExpiry(): void {
    this.tokenService.refreshTokenExpiry();
  }

  /**
   * Token löschen (delegiert zu TokenService)
   */
  clearToken(): void {
    this.tokenService.clearToken();
  }

  /**
   * Email aus JWT Token dekodieren
   */
  getEmailFromToken(): string | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      // JWT hat 3 Teile: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      // Payload dekodieren (mit Base64Url decoding)
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const decodedObj = JSON.parse(decoded);

      // Email aus den Claims abrufen (normalerweise "email" oder "sub")
      return decodedObj.email || decodedObj['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || null;
    } catch (error) {
      console.error('Fehler beim Dekodieren des JWT Tokens:', error);
      return null;
    }
  }

  /**
   * Prüfe ob Benutzer authentifiziert ist
   */
  isLoggedIn(): boolean {
    return this.isAuthenticated();
  }

  /**
   * Passwort-Änderung anfordern (Code senden)
   */
  requestPasswordChangeCode(email: string) {
    // In Entwicklungsumgebung: Code lokal generieren und in Console ausgeben
    if (this.isDevelopment()) {
      const verificationCode = this.generateVerificationCode();
      console.log('===========================================');
      console.log('📧 DEVELOPMENT MODE - Passwort-Änderungscode:');
      console.log(`Email: ${email}`);
      console.log(`Code: ${verificationCode}`);
      console.log('===========================================');
      
      // Speichern für spätere Verifikation
      localStorage.setItem(`passwordChangeCode_${email}`, verificationCode);
      
      return of({
        success: true,
        message: 'Passwort-Änderungscode wurde gesendet. Code wurde in der Console ausgegeben.'
      }).pipe(
        tap((response) => {
          this.errorMessage.set(null);
        })
      );
    }

    // Produktion: Backend-API verwenden
    return this.http.post<AuthResponse>(`${this.apiUrl}/request-password-change`, { email }).pipe(
      tap((response) => {
        this.errorMessage.set(null);
      }),
      catchError((error) => {
        const message = error.error?.message || 'Anforderung fehlgeschlagen';
        this.errorMessage.set(message);
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Passwort-Änderungscode verifizieren
   */
  verifyPasswordChangeCode(email: string, code: string) {
    // In Entwicklungsumgebung: Code lokal überprüfen
    if (this.isDevelopment()) {
      const storedCode = localStorage.getItem(`passwordChangeCode_${email}`);
      if (storedCode === code) {
        return of({
          success: true,
          message: 'Code überprüft'
        }).pipe(
          tap((response) => {
            this.errorMessage.set(null);
          })
        );
      } else {
        const errorMessage = 'Ungültiger Verifizierungscode';
        this.errorMessage.set(errorMessage);
        return throwError(() => new Error(errorMessage));
      }
    }

    // Produktion: Backend-API verwenden
    return this.http.post<AuthResponse>(`${this.apiUrl}/verify-password-change`, { email, code }).pipe(
      tap((response) => {
        this.errorMessage.set(null);
      }),
      catchError((error) => {
        const message = error.error?.message || 'Code-Verifizierung fehlgeschlagen';
        this.errorMessage.set(message);
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Passwort-Änderung bestätigen (für eingeloggte Benutzer - nur 2 Parameter!)
   */
  confirmPasswordChange(email: string, newPassword: string) {
    // In Entwicklungsumgebung: Passwort lokal aktualisieren
    if (this.isDevelopment()) {
      const registeredUserJson = localStorage.getItem(`registeredUser_${email}`);
      if (registeredUserJson) {
        const registeredUser = JSON.parse(registeredUserJson);
        registeredUser.password = newPassword;
        localStorage.setItem(`registeredUser_${email}`, JSON.stringify(registeredUser));
        
        // Code löschen
        localStorage.removeItem(`passwordChangeCode_${email}`);
        
        return of({
          success: true,
          message: 'Passwort erfolgreich geändert'
        }).pipe(
          tap((response) => {
            this.errorMessage.set(null);
          })
        );
      }
      
      const errorMessage = 'Benutzer nicht gefunden';
      this.errorMessage.set(errorMessage);
      return throwError(() => new Error(errorMessage));
    }

    // Produktion: Backend-API verwenden
    // WICHTIG: Nur email und newPassword - KEIN Code!
    return this.http.post<AuthResponse>(`${this.apiUrl}/confirm-password-change`, { 
      email, 
      newPassword 
    }).pipe(
      tap((response) => {
        this.errorMessage.set(null);
      }),
      catchError((error) => {
        const message = error.error?.message || 'Passwort-Änderung fehlgeschlagen';
        this.errorMessage.set(message);
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Anforderung für Passwort-Reset mit neuem Passwort
   */
  requestPasswordReset(email: string, newPassword: string) {
    // In Entwicklungsumgebung: Code generieren und in Console ausgeben
    if (this.isDevelopment()) {
      const resetCode = this.generateVerificationCode();
      console.log('===========================================');
      console.log('📧 DEVELOPMENT MODE - Passwort-Reset-Code:');
      console.log(`Email: ${email}`);
      console.log(`Code: ${resetCode}`);
      console.log('===========================================');
      
      localStorage.setItem(`passwordResetCode_${email}`, resetCode);
      localStorage.setItem(`resetPassword_${email}`, newPassword);
      
      return of({
        success: true,
        message: 'Passwort-Änderungscode wurde gesendet'
      }).pipe(
        tap((response) => {
          this.errorMessage.set(null);
        })
      );
    }

    // Produktion: Backend-API verwenden
    return this.http.post<AuthResponse>(`${this.apiUrl}/request-password-change`, { email }).pipe(
      tap((response) => {
        this.errorMessage.set(null);
        if (response.success) {
          // Speichere Passwort temporär für Verifikationsschritt
          sessionStorage.setItem('resetPassword', newPassword);
        }
      }),
      catchError((error) => {
        const message = error.error?.message || 'Anforderung fehlgeschlagen';
        this.errorMessage.set(message);
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Bestätige Passwort-Reset mit Code
   */
  confirmPasswordReset(email: string, newPassword: string, code: string) {
    // In Entwicklungsumgebung: Code lokal überprüfen
    if (this.isDevelopment()) {
      const storedCode = localStorage.getItem(`passwordResetCode_${email}`);
      if (storedCode === code) {
        // Erfolgreicher Reset
        localStorage.removeItem(`passwordResetCode_${email}`);
        localStorage.removeItem(`resetPassword_${email}`);
        
        return of({
          success: true,
          message: 'Passwort erfolgreich zurückgesetzt'
        }).pipe(
          tap((response) => {
            this.errorMessage.set(null);
          })
        );
      } else {
        const errorMessage = 'Ungültiger Verifizierungscode';
        this.errorMessage.set(errorMessage);
        return throwError(() => new Error(errorMessage));
      }
    }

    // Produktion: Backend-API verwenden
    return this.http.post<AuthResponse>(`${this.apiUrl}/confirm-password-change`, { 
      email, 
      code,
      newPassword 
    }).pipe(
      tap((response) => {
        this.errorMessage.set(null);
      }),
      catchError((error) => {
        const message = error.error?.message || 'Passwort-Reset fehlgeschlagen';
        this.errorMessage.set(message);
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Prüfe ob Entwicklungsumgebung
   * GEÄNDERT: Immer false zurückgeben, um echte API zu verwenden
   */
  private isDevelopment(): boolean {
    return false; // Immer API verwenden, nicht localStorage
  }

  /**
   * Prüfe ob Produktionsumgebung
   */
  private isProduction(): boolean {
    return true; // Immer als Produktion behandeln = API verwenden
  }

  /**
   * Generiere einen 6-stelligen Verifizierungscode
   */
  private generateVerificationCode(): string {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  /**
   * Starte Timer für automatischen Token-Ablauf-Check (alle 10 Sekunden)
   */
  private startTokenExpiryCheck() {
    // Verhindere mehrfache Timer
    this.stopTokenExpiryCheck();

    this.tokenExpiryCheckInterval = setInterval(() => {
      const tokenDataStr = sessionStorage.getItem('authToken');
      if (!tokenDataStr) {
        // Kein Token -> Logout
        this.handleTokenExpiry();
        return;
      }

      try {
        const tokenData = JSON.parse(tokenDataStr);
        // Prüfe ob Token abgelaufen ist
        if (Date.now() > tokenData.expiresAt) {
          this.handleTokenExpiry();
        }
      } catch {
        this.handleTokenExpiry();
      }
    }, 10000); // Alle 10 Sekunden prüfen
  }

  /**
   * Stoppe Timer für Token-Ablauf-Check
   */
  private stopTokenExpiryCheck() {
    if (this.tokenExpiryCheckInterval) {
      clearInterval(this.tokenExpiryCheckInterval);
      this.tokenExpiryCheckInterval = null;
    }
  }

  /**
   * Behandle abgelaufenes Token: Logout + Redirect zur Login-Seite
   */
  private handleTokenExpiry() {
    this.stopTokenExpiryCheck();
    this.removeActivityListeners();
    this.clearToken();
    this.isAuthenticated.set(false);
    this.user.set(null);
    this.router.navigate(['/login']);
  }

  /**
   * Setup Event-Listener für User-Aktivitäten (Eingaben, Klicks, etc.)
   * Diese verlängern automatisch das Token-Ablaufdatum
   */
  private setupActivityListeners() {
    // Throttle: Nur einmal alle 5 Sekunden Token verlängern bei Aktivität
    let lastRefresh = 0;
    const throttleMs = 5000;

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastRefresh > throttleMs && this.isAuthenticated()) {
        this.refreshTokenExpiry();
        lastRefresh = now;
      }
    };

    // Event-Listener für verschiedene User-Aktivitäten
    document.addEventListener('click', handleActivity);
    document.addEventListener('keydown', handleActivity);
    document.addEventListener('input', handleActivity);
    document.addEventListener('mousemove', handleActivity);

    // Speichere Handler-Funktion für späteres Entfernen
    (this as any).activityHandler = handleActivity;
  }

  /**
   * Entferne Event-Listener für User-Aktivitäten
   */
  private removeActivityListeners() {
    const handler = (this as any).activityHandler;
    if (handler) {
      document.removeEventListener('click', handler);
      document.removeEventListener('keydown', handler);
      document.removeEventListener('input', handler);
      document.removeEventListener('mousemove', handler);
      delete (this as any).activityHandler;
    }
  }

  /**
   * Update help sidebar visibility preference
   */
  updateHelpSidebarPreference(visible: boolean) {
    return this.http.patch<AuthResponse>(`${this.apiUrl}/help-sidebar`, { visible }).pipe(
      tap(() => {
        this.helpSidebarVisible.set(visible);
      }),
      catchError((error) => {
        console.error('Error updating help sidebar preference:', error);
        return throwError(() => error);
      })
    );
  }
}
